import { Inject, Injectable, NgZone, Renderer2 } from '@angular/core';
import { AmbientScribeWidgetGandalfService } from '@gandalf/services';
import { firstValueFrom, Subject } from 'rxjs';
import { DocumentService } from 'majora';
import { AmbientScribeWidgetConfigurationResponse } from '@gandalf/model/ambient-scribe-widget-configuration-response';
import { WINDOW } from '@core/injection-tokens/environment-tokens';
import { PatientNamePipe } from '@shared/pipes/patient-name/patient-name.pipe';
import { SecurityManagerService } from '@core/security-manager/security-manager.service';
import { PatientNameResponse } from '@gandalf/model/patient-name-response';

export interface AmbientScribeWidgetEncounterContext {
	encounterId: number;
	generalPrompt: string;
	patient: PatientNameResponse;
	templateName: string;
	tests: AmbientScribeWidgetEncounterTestContext[];
}

export interface AmbientScribeWidgetEncounterTestContext {
	name: string;
	generatedPrompt: string;
}

export interface AmbientScribeWidgetEncounterDocument {
	encounterId: number;
	sessionIdentifier: string;
	fields: AmbientScribeWidgetEncounterFieldResult[];
}

export interface AmbientScribeWidgetEncounterFieldResult {
	testName: string;
	fieldName: string;
	fieldValue: string;
}

@Injectable({
	providedIn: 'root',
})
export class AmbientScribeWidgetService {

	private initialized = false;
	private scribe: any = undefined;
	private config: AmbientScribeWidgetConfigurationResponse;
	private lastSessionIdentifier: string = null;
	private encounterDocumentCreatedSubject = new Subject<AmbientScribeWidgetEncounterDocument>();

	readonly encounterDocumentCreated = this.encounterDocumentCreatedSubject.asObservable();

	constructor(
		private ambientScribeWidgetGandalfService: AmbientScribeWidgetGandalfService,
		private documentService: DocumentService,
		@Inject(WINDOW) private window: any,
		private patientNamePipe: PatientNamePipe,
		private securityManager: SecurityManagerService,
		private ngZone: NgZone,
	) {
	}

	async openEncounterScribe(renderer: Renderer2, context: AmbientScribeWidgetEncounterContext) {
		if (!this.initialized) {
			await this.initializeScribeAssets(renderer);
			this.initialized = true;
		}
		this.scribe?.unmount();
		this.scribe = new this.window.AmbientScribeWidget({
			id: 'ambient-scribe-widget',
			hostSeamlessAuth: {
				getTokenCallback: async () => await firstValueFrom(this.ambientScribeWidgetGandalfService.getJwtToken()),
			},
			initialContext: {
				templates: [
					{
						name: context.templateName,
						description: context.templateName,
						version: '1',
						customerId: this.securityManager.getUserSession().practiceId.toString(),
						createdBy: this.securityManager.getUserSession().userEntityId.toString(),
						documentType: 'CUSTOM',
						systemPrompt: context.generalPrompt,
						structure: {
							documentType: 'CUSTOM',
							sections: context.tests.map(test => ({
								key: test.name,
								type: 'text',
								required: true,
								prompt: test.generatedPrompt,
							})),
						},
					},
				],
			},
		});
		this.scribe.on(this.window.AmbientScribeEvents.LISTENER_NAMES.READY, () => {
			this.scribe.emit(this.window.AmbientScribeEvents.EMITTER_NAMES.SET_ENCOUNTER, {
				appointment: {
					id: context.encounterId.toString(),
					patientName: this.patientNamePipe.transform(context.patient),
					title: context.templateName,
				},
			});
		});
		this.scribe.on(this.window.AmbientScribeEvents.LISTENER_NAMES.SAVE_SESSION_ID, data => this.lastSessionIdentifier = data.sessionId);
		this.scribe.on(this.window.AmbientScribeEvents.LISTENER_NAMES.EXPORT_DOCUMENT, data => this.ngZone.run(() => this.encounterDocumentCreatedSubject.next({
			encounterId: context.encounterId,
			sessionIdentifier: this.lastSessionIdentifier,
			fields: this.buildFieldResults(data.document),
		})));
		this.scribe.mount();
	}

	private async initializeScribeAssets(renderer: Renderer2) {
		this.config = await firstValueFrom(this.ambientScribeWidgetGandalfService.getConfiguration());
		await Promise.all([
			this.documentService.loadStylesheet(renderer, this.config.cssUrl),
			this.documentService.loadJavascript(renderer, this.config.jsUrl, true),
		]);
	}

	private buildFieldResults(document: any) {
		const fields: AmbientScribeWidgetEncounterFieldResult[] = [];
		for (const [testName, testJson] of Object.entries<string>(document.content)) {
			// assumes the scribe will generate a JSON response with the name of the field as the key, e.g.: {"OD GAT": 11, "Disc Type OD": ["II","V"]}
			const start = testJson.indexOf('{');
			const end = testJson.lastIndexOf('}');
			if (start !== -1 && end !== -1 && end > start) {
				let testFields: any;
				try {
					testFields = JSON.parse(testJson.substring(start, end + 1));
				} catch (error) {
					// ignore any tests that have an invalid JSON response
					console.error('invalid ai scribe json', testJson, error);
					continue;
				}
				// ignore any results with "fieldName" where the template structure was returned instead of just the field values
				if (Object.keys(testFields).includes('fieldName')) {
					continue;
				}
				for (const [fieldName, jsonValue] of Object.entries<any>(testFields)) {
					const jsonValues: any[] = Array.isArray(jsonValue) ? jsonValue : [jsonValue];
					for (const jsonValue of jsonValues) {
						// ignore any nested objects and arrays; the field value must be a string | number | boolean
						if (typeof jsonValue === 'object') {
							continue;
						}
						const fieldValue = jsonValue?.toString()?.trim();
						// ignore any empty field values
						if (fieldValue?.length > 0) {
							fields.push({
								testName: testName.trim(),
								fieldName: fieldName.trim(),
								fieldValue,
							});
						}
					}
				}
			}
		}
		return fields;
	}
}
